import {
  Award,
  BarChart3,
  Compass,
  FileBarChart,
  Layers,
  Microscope,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export const KIND_LABELS = [
  "研究方向评价",
  "研究方法评价",
  "项目对标评价",
  "项目综合评价",
  "创新生成系统",
  "创新决策报告",
] as const;

export type KindLabel = (typeof KIND_LABELS)[number];
export type RecordStatus = "优化中" | "已完成";

export type DimensionScore = { label: string; score: number; note: string };

export type ScoreBar = {
  label: string;
  score: number;
  tone?: "primary" | "emerald" | "amber" | "violet" | "cyan" | "pink";
};

export type ChatMsg = {
  id: string;
  role: "assistant" | "user";
  content: string;
  time: string;
};

export type EvaluationRecord = {
  id: string;
  title: string;
  kind: KindLabel;
  date: string;
  score: number;
  status: RecordStatus;
  summary: string;
  dimensions: DimensionScore[];
  highlights: string[];
  improvements: string[];

  /** 详情页：多轮对话 */
  chat?: {
    suggestions?: string[];
    messages: ChatMsg[];
  };

  /** 详情页：评审报告 */
  report?: {
    inputTitle?: string;
    inputContent?: string;
    scoreBars?: ScoreBar[];
    conclusion?: string;
    keyIssues?: string[];
    advantages?: string[];
    risks?: string[];
  };

  /** 版本化（v1/v2...），用于“持续优化迭代” */
  versions?: EvaluationVersion[];
};

export type EvaluationInput =
  | { kind: "file"; fileName: string }
  | { kind: "text"; text: string }
  | { kind: "link"; url: string };

export type EvaluationVersion = {
  id: string; // v1 / v2 / ...
  createdAt: string; // yyyy-mm-dd hh:mm
  status: RecordStatus;
  score: number;
  kind: KindLabel;
  input?: EvaluationInput;
  report?: EvaluationRecord["report"];
  chat?: EvaluationRecord["chat"];
  progress?: {
    nodes: Array<{ id: string; title: string; status: "queued" | "running" | "done" | "failed"; costMs?: number; logs?: string[] }>;
  };
};

function fmtNow() {
  return new Date().toISOString().replace("T", " ").slice(0, 16);
}

function ensureVersions(rec: EvaluationRecord): EvaluationRecord & { versions: EvaluationVersion[] } {
  if (rec.versions && rec.versions.length) return rec as EvaluationRecord & { versions: EvaluationVersion[] };
  const v1: EvaluationVersion = {
    id: "v1",
    createdAt: `${rec.date} 14:30`,
    status: rec.status,
    score: rec.score,
    kind: rec.kind,
    report: rec.report,
    chat: rec.chat,
  };
  return { ...rec, versions: [v1] };
}

export function getRecordWithVersions(id: string): (EvaluationRecord & { versions: EvaluationVersion[] }) | undefined {
  const rec = getEvaluationRecord(id);
  if (!rec) return undefined;
  return ensureVersions(rec);
}

export function getRecordVersion(rec: EvaluationRecord & { versions: EvaluationVersion[] }, versionId: string | null | undefined): EvaluationVersion {
  if (!versionId) return rec.versions[0]!;
  return rec.versions.find((v) => v.id === versionId) ?? rec.versions[0]!;
}

export function createNextVersionId(rec: EvaluationRecord & { versions: EvaluationVersion[] }): string {
  const nums = rec.versions
    .map((v) => Number(String(v.id).replace(/^v/i, "")))
    .filter((n) => Number.isFinite(n));
  const next = (nums.length ? Math.max(...nums) : 1) + 1;
  return `v${next}`;
}

export function appendRecordVersion(recordId: string, next: EvaluationVersion) {
  const all = getAllEvaluationRecords();
  const idx = all.findIndex((r) => r.id === recordId);
  if (idx < 0) return;
  const cur = ensureVersions(all[idx]!);
  const updated: EvaluationRecord = {
    ...cur,
    // keep top-level summary/status/score aligned with latest version for list display
    status: next.status,
    score: next.score,
    kind: next.kind,
    report: next.report,
    chat: next.chat,
    versions: [...cur.versions, next],
  };
  const nextAll = all.slice();
  nextAll[idx] = updated;
  persist(nextAll);
}

export function updateRecordVersion(recordId: string, versionId: string, patch: Partial<EvaluationVersion>) {
  const all = getAllEvaluationRecords();
  const idx = all.findIndex((r) => r.id === recordId);
  if (idx < 0) return;
  const cur = ensureVersions(all[idx]!);
  const versions = cur.versions.map((v) => (v.id === versionId ? { ...v, ...patch } : v));
  const latest = versions[versions.length - 1]!;
  const updated: EvaluationRecord = {
    ...cur,
    status: latest.status,
    score: latest.score,
    kind: latest.kind,
    report: latest.report,
    chat: latest.chat,
    versions,
  };
  const nextAll = all.slice();
  nextAll[idx] = updated;
  persist(nextAll);
}

const LS_KEY = "zsk_innovation_records_v1";

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function persist(list: EvaluationRecord[]) {
  try {
    sessionStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

function loadPersisted(): EvaluationRecord[] | null {
  if (typeof window === "undefined") return null;
  return safeParse<EvaluationRecord[]>(sessionStorage.getItem(LS_KEY));
}

export const EVALUATION_RECORDS: EvaluationRecord[] = [
  {
    id: "eval-240405-01",
    title: "多源信息智能平台界面设计方案研究",
    kind: "项目综合评价",
    date: "2026-04-05",
    score: 74,
    status: "优化中",
    summary:
      "申报书对创新点与落地路径表述偏散，管理层可读性尚可，但技术指标与里程碑的对照关系需加强；建议在修订稿中收敛为 3 条以内核心创新表述，并补充与同类项目的差异化证据。",
    dimensions: [
      { label: "立项依据与意义", score: 78, note: "政策契合度较好，量化指标不足" },
      { label: "创新点与可行性", score: 72, note: "创新点较多，聚焦度待提升" },
      { label: "团队与条件", score: 75, note: "团队结构合理，任务分工可再细化" },
      { label: "风险与对策", score: 70, note: "风险识别偏泛，缺少分级" },
    ],
    highlights: ["跨机构协作场景描述完整", "用户旅程图清晰"],
    improvements: ["将创新点合并为「平台架构 + 交互范式」两条主线", "补充里程碑与验收口径"],
    chat: {
      suggestions: ["把关键问题转成修改清单", "给我一段管理层摘要", "里程碑怎么写更像“验收口径”", "如何突出差异化证据链？"],
      messages: [
        { id: "c1", role: "user", time: "09:02", content: "这份方案评审偏低，主要卡在哪？" },
        {
          id: "c2",
          role: "assistant",
          time: "09:03",
          content: "主要在两点：1）创新点表述偏散，缺少“主线贡献”；2）技术指标与里程碑没有形成可验收闭环。建议先收敛 2 条核心贡献，再补齐量化验收口径。",
        },
        { id: "c3", role: "user", time: "09:05", content: "你帮我把创新点收敛成两条主线？" },
        {
          id: "c4",
          role: "assistant",
          time: "09:06",
          content: "可以：主线 A「平台架构与数据治理」：统一数据/模型/权限的架构与可追溯链路；主线 B「交互范式与协作流程」：以任务流为中心的协作评审与可解释输出。",
        },
        { id: "c5", role: "user", time: "09:08", content: "里程碑怎么写才不空？" },
        {
          id: "c6",
          role: "assistant",
          time: "09:09",
          content: "用“动作 + 指标 + 口径 + 证据”写法：例如“完成××模块联调（延迟≤200ms，95th）并提交可复现实验脚本与日志作为证据”。",
        },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本方案面向科研智能平台的多源信息整合与协作评审场景，提出一套可追溯的知识沉淀与评价流程。方案包含：侧栏信息架构、创新评价工作台、记录归档与分享机制；并给出核心页面原型与关键交互流程。",
      scoreBars: [
        { label: "创新性", score: 72, tone: "amber" },
        { label: "可行性", score: 76, tone: "emerald" },
        { label: "对标", score: 74, tone: "primary" },
      ],
      conclusion:
        "整体架构与交互思路完整，但创新点主线不够聚焦，指标与里程碑缺少可验收口径。建议在修订中优先“收敛创新点 + 补齐量化指标 + 加强差异化证据”。",
      keyIssues: [
        "创新点数量多但不成主线，管理层难以一眼抓住价值",
        "关键指标/里程碑缺少量化口径与证据交付物说明",
        "对标样本不足：同类平台、竞品与论文/专利证据链不完整",
      ],
      advantages: ["信息架构清晰，关键任务路径明确", "协作与归档流程闭环", "原型覆盖核心页面"],
      risks: ["指标不可验收导致推进困难", "差异化证据不足导致评审质疑", "权限与合规边界需提前定义"],
    },
  },
  {
    id: "eval-240402-02",
    title: "基于强化学习的蛋白质折叠路径优化",
    kind: "研究方法评价",
    date: "2026-04-02",
    score: 88,
    status: "已完成",
    summary:
      "方法设计严谨，训练与评测协议说明充分；消融实验与基线对照覆盖主要变量，结论可信。建议在正文中突出可复现实验配置与随机种子策略。",
    dimensions: [
      { label: "方法科学性", score: 90, note: "问题形式化清晰" },
      { label: "实验设计", score: 88, note: "消融与统计充分" },
      { label: "可复现性", score: 85, note: "代码与数据开放计划已写" },
      { label: "局限与展望", score: 87, note: "坦诚、可改进" },
    ],
    highlights: ["基线选取具有代表性", "误差分析到位"],
    improvements: ["可补充大规模结构集上的泛化曲线"],
    chat: {
      suggestions: ["帮我写一段审稿回复", "消融实验怎么补最有说服力？", "怎么强调可复现性？", "给出实验设置的最小披露清单"],
      messages: [
        { id: "c1", role: "user", time: "11:22", content: "评审说我可复现性还不够，你建议补什么？" },
        {
          id: "c2",
          role: "assistant",
          time: "11:23",
          content: "优先补：随机种子策略、数据划分脚本、超参表（含搜索范围）、训练硬件/时长、评测口径与统计显著性检验方法，并提供一键复现实验入口（Docker/conda）。",
        },
        { id: "c3", role: "user", time: "11:25", content: "消融实验要怎么选变量？" },
        {
          id: "c4",
          role: "assistant",
          time: "11:26",
          content: "围绕贡献点拆：1）奖励函数设计；2）策略网络结构；3）采样/探索机制；4）训练日程（warmup、lr）。每次只改一个变量，并给出 3 次重复的均值±方差。",
        },
        { id: "c5", role: "user", time: "11:29", content: "给我一句话的核心贡献？" },
        {
          id: "c6",
          role: "assistant",
          time: "11:30",
          content: "提出一种将强化学习与能量约束结合的折叠路径优化框架，在相同计算预算下实现更稳定的收敛与更优的结构质量。",
        },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本文研究蛋白质折叠路径优化问题，采用强化学习在构象空间中搜索低能态路径。方法包含：状态编码、动作空间设计、奖励函数与训练策略，并在公开结构集上与多种基线对比。",
      scoreBars: [
        { label: "创新性", score: 88, tone: "primary" },
        { label: "可行性", score: 90, tone: "emerald" },
        { label: "对标", score: 86, tone: "amber" },
      ],
      conclusion:
        "方法设计严谨，实验对比充分，结论可信。建议进一步强化可复现性披露与更大规模数据集上的泛化曲线。",
      keyIssues: ["复现实验配置披露可更完整（种子/硬件/超参）", "可补充更大规模结构集的泛化评测", "讨论局限时可加入失败案例分析"],
      advantages: ["奖励函数与训练策略解释清晰", "消融实验覆盖主要变量", "误差分析到位，结论稳健"],
      risks: ["可复现性不足会影响采纳", "泛化验证不充分会被质疑过拟合"],
    },
  },
  {
    id: "eval-240411-03",
    title: "量子材料器件化路径创新决策报告",
    kind: "创新决策报告",
    date: "2026-04-11",
    score: 86,
    status: "已完成",
    summary:
      "决策报告层次完整：结论—证据—风险链条闭合；器件工艺路径与时间表一致性较好。合规注意事项已在风险提示中单列，便于管理层批复。",
    dimensions: [
      { label: "结论可追溯性", score: 88, note: "引用证据编号清晰" },
      { label: "风险分级", score: 84, note: "技术风险细分到位" },
      { label: "资源与里程碑", score: 85, note: "预算科目对齐度高" },
      { label: "决策建议可操作性", score: 87, note: "下一步动作明确" },
    ],
    highlights: ["出口管制条款单独提示", "里程碑与责任人映射完整"],
    improvements: ["可增加备选供应商情景分析一页摘要"],
    chat: {
      suggestions: ["帮我生成一页管理层摘要", "风险分级怎么写更像决策报告", "给出推荐行动路径 4 条", "补一段合规提醒"],
      messages: [
        { id: "c1", role: "user", time: "14:01", content: "领导只看一页摘要，怎么写？" },
        {
          id: "c2",
          role: "assistant",
          time: "14:02",
          content: "结构建议：结论（是否推进）→ 3 条关键证据 → 风险分级（高/中/低+应对）→ 资源与里程碑 → 需要领导拍板的 1-2 个点。",
        },
        { id: "c3", role: "user", time: "14:05", content: "风险分级给我个模板。" },
        {
          id: "c4",
          role: "assistant",
          time: "14:06",
          content: "按“触发条件/影响/概率/监控指标/应对措施/责任人/时间”写，并标注等级：高（需调整方案）、中（需补充验证）、低（常规跟踪）。",
        },
        { id: "c5", role: "user", time: "14:09", content: "行动路径怎么写更可执行？" },
        {
          id: "c6",
          role: "assistant",
          time: "14:10",
          content: "用“动作 + 输出物 + 截止时间 + Owner”写，例如“完成合规审查清单与材料归档（T+7，法务/合规）”。",
        },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本决策报告围绕量子材料器件化路径，汇总研究现状、关键证据、路线选择与风险分级，输出推荐行动路径与里程碑计划，用于管理层批复与资源配置。",
      scoreBars: [
        { label: "创新性", score: 86, tone: "primary" },
        { label: "可行性", score: 85, tone: "emerald" },
        { label: "对标", score: 84, tone: "amber" },
      ],
      conclusion:
        "报告结构完整，结论—证据—风险链条闭合，适合决策层快速阅读。建议补充备选供应商/器件路线的情景分析以提升抗风险能力。",
      keyIssues: ["备选路线的情景分析可增加 1 页", "对外合作与数据共享边界需更明确", "关键里程碑的验收口径建议量化"],
      advantages: ["证据引用编号清晰可追溯", "风险分级到位，便于跟踪", "行动路径明确，可直接落表"],
      risks: ["合规条款变化带来不确定性", "供应链波动影响进度"],
    },
  },
  {
    id: "eval-240328-04",
    title: "碳中和背景下新型钙钛矿太阳能电池项目立项",
    kind: "项目对标评价",
    date: "2026-03-28",
    score: 83,
    status: "已完成",
    summary:
      "与近三年同类立项对比：技术指标处于中上分位；经费结构与任务分解匹配度优良。建议在预算说明中强化测试与外协科目的必要性论述。",
    dimensions: [
      { label: "同类项目对标", score: 85, note: "检索口径一致" },
      { label: "经费合理性", score: 82, note: "设备与外协占比合理" },
      { label: "重复性与差异化", score: 81, note: "差异化三条论据清晰" },
      { label: "指标先进性", score: 84, note: "效率指标优于均值" },
    ],
    highlights: ["对标样本覆盖中英期刊与专利库", "经费科目映射表直观"],
    improvements: ["补充极端情景下的预算弹性说明"],
    chat: {
      suggestions: ["经费合理性怎么解释更强？", "对标样本范围怎么写？", "差异化三条论据再强化一下", "给我一段立项答辩开场"],
      messages: [
        { id: "c1", role: "user", time: "16:12", content: "对标样本我应该怎么披露？" },
        {
          id: "c2",
          role: "assistant",
          time: "16:13",
          content: "写清：时间范围（近3年）、数据库（Web of Science/专利库等）、关键词与筛选条件、最终纳入数量与代表性说明，并给出 Top N 列表作为附件。",
        },
        { id: "c3", role: "user", time: "16:15", content: "经费合理性经常被问，怎么答？" },
        {
          id: "c4",
          role: "assistant",
          time: "16:16",
          content: "用“任务—资源—指标”对照表：每个经费科目对应哪条任务、产出什么指标/数据，为什么必须自建/外协，附替代方案与成本对比。",
        },
        { id: "c5", role: "user", time: "16:18", content: "预算弹性怎么补？" },
        { id: "c6", role: "assistant", time: "16:19", content: "给出两档情景：材料涨价/设备延期时的替代路线与压缩项，说明不影响关键指标达成。" },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本立项聚焦新型钙钛矿太阳能电池，目标在碳中和背景下实现更高效率与更低成本。材料体系、工艺路线与测试方案已给出，并提供与同类项目的对标检索与经费结构说明。",
      scoreBars: [
        { label: "创新性", score: 83, tone: "primary" },
        { label: "可行性", score: 82, tone: "emerald" },
        { label: "对标", score: 85, tone: "amber" },
      ],
      conclusion:
        "技术指标处于中上分位，经费结构与任务分解匹配度较好。建议补充预算弹性说明与极端情景下的替代路线。",
      keyIssues: ["预算弹性与替代路线需补充", "对标样本列表建议附录化", "测试口径与验收指标建议更量化"],
      advantages: ["对标检索口径一致", "经费映射表直观易解释", "差异化论据较清晰"],
      risks: ["材料价格波动影响预算", "测试设备排期影响里程碑"],
    },
  },
  {
    id: "eval-240412-05",
    title: "拓扑量子材料输运特性与器件应用研究",
    kind: "研究方向评价",
    date: "2026-04-12",
    score: 87,
    status: "已完成",
    summary:
      "方向与国家重大专项指南契合度高；前沿问题拆解层次分明，与国家实验室现有布局互补性强。建议在叙述上更早给出「为何是现在」的时间窗口论证。",
    dimensions: [
      { label: "战略契合度", score: 90, note: "与指南条目逐项对齐" },
      { label: "前沿性与首创性", score: 86, note: "文献评述扎实" },
      { label: "潜在影响面", score: 85, note: "器件链条延展清晰" },
      { label: "合规与伦理", score: 87, note: "敏感方向提示完备" },
    ],
    highlights: ["顶层叙事与国家专项对齐自然", "潜在管制条款预警到位"],
    improvements: ["可增加一页「窗口期」论证图示"],
    chat: {
      suggestions: ["帮我写窗口期论证", "出口管制怎么写更合规？", "给出 3 条关键发现", "把亮点变成答辩话术"],
      messages: [
        { id: "c1", role: "user", time: "13:40", content: "窗口期论证怎么写更有说服力？" },
        {
          id: "c2",
          role: "assistant",
          time: "13:41",
          content: "用“三段式”：趋势（近2-3年突破）→ 资源（国内外设备/数据到位）→ 风险（延后会错过标准/专利窗口）。最好配一张时间轴图。",
        },
        { id: "c3", role: "user", time: "13:44", content: "出口管制相关我怕写错。" },
        {
          id: "c4",
          role: "assistant",
          time: "13:45",
          content: "建议：只写事实与流程，不做法律结论。列出潜在敏感点、拟采用的合规审查流程与责任人，并注明“最终以合规部门审查为准”。",
        },
        { id: "c5", role: "user", time: "13:48", content: "关键发现给我三条。" },
        {
          id: "c6",
          role: "assistant",
          time: "13:49",
          content: "1）方向与指南条目逐项对齐，战略契合度高；2）前沿问题拆解清晰，证据链闭合；3）敏感方向合规提示完善，便于后续发表。",
        },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本研究聚焦拓扑量子材料的输运特性与器件应用，围绕前沿问题拆解、国家重大专项契合度、潜在影响面与合规风险进行系统评估，并给出推荐行动路径。",
      scoreBars: [
        { label: "创新性", score: 89, tone: "primary" },
        { label: "可行性", score: 83, tone: "emerald" },
        { label: "对标", score: 92, tone: "amber" },
      ],
      conclusion:
        "方向契合度高、前沿性强，建议补充“为何是现在”的窗口期图示，并将行动路径落到可验收里程碑。",
      keyIssues: ["窗口期论证建议图示化", "行动路径需绑定里程碑与Owner", "对标样本披露建议更透明"],
      advantages: ["前沿问题拆解层次分明", "与国家布局互补性强", "合规风险提示明确"],
      risks: ["合规条款变化可能影响发表节奏", "关键设备排期影响实验进度"],
    },
  },
  {
    id: "eval-240408-06",
    title: "Bayesian Nonparametric Few-shot Learning",
    kind: "创新生成系统",
    date: "2026-04-08",
    score: 91,
    status: "已完成",
    summary:
      "Idea 生成质量与跨任务迁移叙事一致；多条备选假设均有文献锚点，便于评审追问时答辩。建议在首轮答复中收敛术语层级，突出一条主线算法贡献。",
    dimensions: [
      { label: "创意多样性", score: 92, note: "候选路径覆盖充分" },
      { label: "跨领域迁移", score: 90, note: "类比有据" },
      { label: "可验证性", score: 89, note: "实验建议具体" },
      { label: "表达清晰度", score: 91, note: "条目结构化良好" },
    ],
    highlights: ["三条 Idea 彼此之间互补性强", "追问预案列表可用"],
    improvements: ["压缩篇幅约 15% 更易管理层阅读"],
    chat: {
      suggestions: ["把三条 Idea 收敛成一条主线", "给出答辩追问清单", "生成一页 proposal", "列出验证实验最小集合"],
      messages: [
        { id: "c1", role: "user", time: "18:20", content: "Idea 很多但怕散，你建议怎么收敛？" },
        {
          id: "c2",
          role: "assistant",
          time: "18:21",
          content: "选一条“主线贡献”（比如非参数先验 + 不确定性估计），其余 Idea 作为 ablation/扩展，形成“主线-支线-备选”三层结构。",
        },
        { id: "c3", role: "user", time: "18:23", content: "追问预案给我 5 条。" },
        {
          id: "c4",
          role: "assistant",
          time: "18:24",
          content: "1）为何要非参数？2）与 ProtoNet/MatchNet 对比？3）复杂度与推理成本？4）失败案例是什么？5）可否迁移到其它模态？",
        },
        { id: "c5", role: "user", time: "18:26", content: "验证实验最小集合怎么选？" },
        {
          id: "c6",
          role: "assistant",
          time: "18:27",
          content: "覆盖三类：少样本分类、跨域迁移、噪声鲁棒。每类 1 个数据集 + 2 个基线 + 1 个消融，保证结论可复核。",
        },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本想法提出一种 Bayesian 非参数的 few-shot 学习框架，强调不确定性建模与跨任务迁移。给出多条备选假设、可验证实验建议与潜在跨领域应用场景。",
      scoreBars: [
        { label: "创新性", score: 92, tone: "primary" },
        { label: "可行性", score: 90, tone: "emerald" },
        { label: "对标", score: 89, tone: "amber" },
      ],
      conclusion:
        "创意质量高且有文献锚点，建议收敛术语层级、突出一条算法主线贡献，并用最小实验集合验证关键假设。",
      keyIssues: ["术语与贡献层级需收敛", "实验集合需更聚焦以快速验证", "失败情景与边界条件可补充"],
      advantages: ["备选假设多且互补", "追问预案可直接用于答辩", "验证建议具体可执行"],
      risks: ["实验范围过大导致周期拉长", "表述不收敛导致评审抓不住主线"],
    },
  },
  {
    id: "eval-240410-07",
    title: "基于 EEspice 的模块化电路仿真框架",
    kind: "项目综合评价",
    date: "2026-04-10",
    score: 79,
    status: "已完成",
    summary:
      "总体方案可行，模块化拆分有利于并行开发；立项文本中验证指标与开源路线尚可强化。建议在下一轮陈述中优先回应评审质疑最多的两条技术指标。",
    dimensions: [
      { label: "综合创新性", score: 78, note: "组合创新清晰" },
      { label: "工程可实现性", score: 82, note: "里程碑务实" },
      { label: "团队与经费", score: 77, note: "人力与设备匹配一般" },
      { label: "成果形态", score: 76, note: "交付物边界可更清" },
    ],
    highlights: ["模块接口定义较清楚", "演示用例完整"],
    improvements: ["压缩非核心功能描述", "补充基线对照数据表"],
    chat: {
      suggestions: ["如何提升项目的创新性与差异化？", "给出可执行的里程碑与验收口径", "补充对标检索与复现方案", "生成一段管理层可读摘要"],
      messages: [
        {
          id: "m1",
          role: "assistant",
          time: "10:16",
          content:
            "综合评分为 79 分。该交付物边界与验证指标的闭环还有提升空间，建议优先补充可量化验收口径，并将开源路线与阶段成果对齐到里程碑表。",
        },
        {
          id: "m2",
          role: "user",
          time: "10:15",
          content: "评审报告的关键点你帮我总结下？",
        },
      ],
    },
    report: {
      inputTitle: "输入内容",
      inputContent:
        "本文提出一种基于 EEspice 的模块化电路仿真框架，通过插件化组件与统一接口实现多类型器件模型的快速组合与复用。目标是提升科研电路仿真效率，支持并行开发与可扩展的验证流程。当前版本包含模块注册、参数配置、仿真调度、结果可视化等能力，并给出了若干示例电路验证。",
      scoreBars: [
        { label: "创新性", score: 81, tone: "primary" },
        { label: "可行性", score: 78, tone: "emerald" },
        { label: "对标", score: 80, tone: "amber" },
      ],
      conclusion:
        "整体方案结构合理，模块化拆分有利于并行与复用；但当前验证指标较抽象、开源与交付物边界需进一步明确。建议将核心贡献收敛为「统一接口 + 可复现实验流程」两条主线，并补充对标数据与验收口径。",
      keyIssues: [
        "验证指标缺少量化口径（性能、准确性、可扩展性）与基线对照",
        "对标样本与检索口径未清晰披露（SPICE/HSIPICE/Spectre 等）",
        "开源/交付物边界不明确：核心模块与示例电路的关系需梳理",
      ],
      advantages: ["模块接口与注册机制清晰，便于扩展新器件模型", "示例电路覆盖基础用例，演示路径完整", "并行开发的工程组织方式可行"],
      risks: ["对标不足导致评审认为创新性偏弱", "验收口径不清导致里程碑难以考核", "开源合规与依赖许可需提前评估"],
    },
  },
];

export function getAllEvaluationRecords(): EvaluationRecord[] {
  const stored = loadPersisted();
  return stored && Array.isArray(stored) ? stored : EVALUATION_RECORDS;
}

export function getEvaluationRecord(id: string): EvaluationRecord | undefined {
  return getAllEvaluationRecords().find((r) => r.id === id);
}

export function addEvaluationRecord(rec: EvaluationRecord) {
  const all = getAllEvaluationRecords();
  persist([rec, ...all]);
}

export function getKindVisual(kind: KindLabel): { Icon: LucideIcon; wrap: string; icolor: string } {
  switch (kind) {
    case "研究方向评价":
      return { Icon: Compass, wrap: "bg-indigo-50", icolor: "text-indigo-600" };
    case "研究方法评价":
      return { Icon: Microscope, wrap: "bg-cyan-50", icolor: "text-cyan-600" };
    case "项目对标评价":
      return { Icon: BarChart3, wrap: "bg-emerald-50", icolor: "text-emerald-600" };
    case "项目综合评价":
      return { Icon: Award, wrap: "bg-amber-50", icolor: "text-amber-600" };
    case "创新生成系统":
      return { Icon: Sparkles, wrap: "bg-violet-50", icolor: "text-violet-600" };
    case "创新决策报告":
      return { Icon: FileBarChart, wrap: "bg-pink-50", icolor: "text-pink-700" };
    default:
      return { Icon: Layers, wrap: "bg-muted", icolor: "text-muted-foreground" };
  }
}

/** 新建流程第一步：可选维度说明（与概览「可用评价维度」一致） */
export const CREATE_DIMENSION_OPTIONS: {
  kind: KindLabel;
  title: string;
  desc: string;
  tags: string[];
  wrap: string;
  icolor: string;
  Icon: LucideIcon;
}[] = [
  {
    kind: "研究方向评价",
    title: "研究方向评价",
    desc: "聚焦科研方向的顶层战略研判",
    tags: ["出口管制分析", "前沿问题分析"],
    wrap: "bg-[#EEF2FF]",
    icolor: "text-[#4F46E5]",
    Icon: Compass,
  },
  {
    kind: "研究方法评价",
    title: "研究方法评价",
    desc: "针对科研方法的技术可行性与科学性进行评估",
    tags: ["技术指标分析"],
    wrap: "bg-cyan-50",
    icolor: "text-cyan-600",
    Icon: Microscope,
  },
  {
    kind: "项目对标评价",
    title: "项目对标评价",
    desc: "科研项目立项、执行阶段的横向对标与合理性校验",
    tags: ["项目查重分析", "经费合理性分析"],
    wrap: "bg-emerald-50",
    icolor: "text-emerald-600",
    Icon: BarChart3,
  },
  {
    kind: "项目综合评价",
    title: "项目综合评价",
    desc: "覆盖科研项目全流程的多场景综合评审",
    tags: ["项目申报分析", "论文审稿分析"],
    wrap: "bg-amber-50",
    icolor: "text-amber-600",
    Icon: Award,
  },
  {
    kind: "创新生成系统",
    title: "创新生成系统",
    desc: "面向科研创新的主动生成与决策支持",
    tags: ["Idea头脑风暴", "交叉领域拓展"],
    wrap: "bg-violet-50",
    icolor: "text-violet-600",
    Icon: Sparkles,
  },
  {
    kind: "创新决策报告",
    title: "创新决策报告",
    desc: "为最终决策提供结构化输出",
    tags: ["创新多维评分", "研究风险等级"],
    wrap: "bg-pink-50",
    icolor: "text-pink-700",
    Icon: FileBarChart,
  },
];
